import { cn } from "@/lib/utils";
import type { AdminReleaseNoteWeek } from "@a7y/types";
import { formatWeekRange } from "./utils";

interface WeekPickerProps {
  weeks: AdminReleaseNoteWeek[];
  selectedIsoWeek: string | null;
  onSelect: (isoWeek: string) => void;
  isLoading: boolean;
}

export function WeekPicker({
  weeks,
  selectedIsoWeek,
  onSelect,
  isLoading,
}: WeekPickerProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800"
          />
        ))}
      </div>
    );
  }

  if (weeks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
        No release notes have been published yet.
      </div>
    );
  }

  return (
    <nav aria-label="Release-note weeks" className="flex flex-col gap-1">
      {weeks.map((week) => {
        const isActive = selectedIsoWeek === week.isoWeek;
        const weekNum = week.isoWeek.split("-W")[1] ?? week.isoWeek;
        const rangeLabel = formatWeekRange(week.rangeStart, week.rangeEnd);
        return (
          <button
            key={week.isoWeek}
            type="button"
            onClick={() => onSelect(week.isoWeek)}
            className={cn(
              "rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
              isActive
                ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-200"
                : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800",
            )}
          >
            <div className="font-semibold">Week {weekNum}</div>
            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              {rangeLabel}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
              {week.releaseCount}{" "}
              {week.releaseCount === 1 ? "release" : "releases"}
              {" \u00B7 "}
              {week.totalItemCount}{" "}
              {week.totalItemCount === 1 ? "item" : "items"}
              {" \u00B7 "}
              {week.userFacingItemCount} public
            </div>
          </button>
        );
      })}
    </nav>
  );
}
